import React from 'react';
import { Check, Circle, X } from 'lucide-react';

const STAGES = [
  { key: 'lead', label: 'Lead' },
  { key: 'documents_submitted', label: 'Documents Submitted' },
  { key: 'under_review', label: 'Under Review' },
  { key: 'offer_letter', label: 'Offer Letter' },
  { key: 'visa_processing', label: 'Visa Processing' },
  { key: 'enrolled', label: 'Enrolled' },
];

export default function WorkflowTracker({ currentStatus, statusHistory = [] }) {
  const isRejected = currentStatus === 'rejected';
  const currentIdx = STAGES.findIndex((s) => s.key === currentStatus);

  const getStepState = (idx) => {
    if (isRejected) return 'rejected';
    if (idx < currentIdx) return 'completed';
    if (idx === currentIdx) return 'active';
    return 'pending';
  };

  return (
    <div data-testid="workflow-tracker">
      {/* Desktop horizontal */}
      <div className="hidden md:flex items-center justify-between">
        {STAGES.map((stage, idx) => {
          const state = getStepState(idx);
          return (
            <React.Fragment key={stage.key}>
              <div className="flex flex-col items-center gap-2 relative">
                <div
                  data-testid={`workflow-step-${stage.key}`}
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                    state === 'completed'
                      ? 'bg-brand text-white shadow-card'
                      : state === 'active'
                      ? 'bg-aqua text-white ring-4 ring-aqua/20 shadow-card'
                      : state === 'rejected'
                      ? 'bg-ember/10 text-danger border border-ember/30'
                      : 'bg-surface border border-contrast/15 text-ink-3'
                  }`}
                >
                  {state === 'completed' ? <Check className="w-5 h-5" /> : state === 'rejected' ? <X className="w-5 h-5" /> : <Circle className="w-4 h-4" />}
                </div>
                <span className={`text-xs font-medium text-center max-w-[90px] ${
                  state === 'active' ? 'text-aqua' : state === 'completed' ? 'text-brand' : 'text-ink-3'
                }`}>
                  {stage.label}
                </span>
              </div>
              {idx < STAGES.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 ${
                  idx < currentIdx ? 'bg-brand' : 'bg-contrast/15'
                }`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Mobile vertical */}
      <div className="md:hidden space-y-3">
        {STAGES.map((stage, idx) => {
          const state = getStepState(idx);
          return (
            <div key={stage.key} className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium shrink-0 ${
                state === 'completed' ? 'bg-brand text-white shadow-card' :
                state === 'active' ? 'bg-aqua text-white ring-4 ring-aqua/20 shadow-card' :
                'bg-surface border border-contrast/15 text-ink-3'
              }`}>
                {state === 'completed' ? <Check className="w-4 h-4" /> : <Circle className="w-3 h-3" />}
              </div>
              <span className={`text-sm ${state === 'active' ? 'font-semibold text-aqua' : state === 'completed' ? 'text-brand' : 'text-ink-3'}`}>
                {stage.label}
              </span>
            </div>
          );
        })}
      </div>

      {isRejected && (
        <div className="mt-4 p-3 bg-ember/10 border border-ember/30 rounded-lg">
          <div className="flex items-center gap-2 text-danger font-medium text-sm">
            <X className="w-4 h-4" /> Application Rejected
          </div>
        </div>
      )}
    </div>
  );
}

export function StatusBadge({ status }) {
  const labels = {
    lead: 'Lead',
    documents_submitted: 'Documents Submitted',
    under_review: 'Under Review',
    offer_letter: 'Offer Letter',
    visa_processing: 'Visa Processing',
    enrolled: 'Enrolled',
    rejected: 'Rejected',
  };

  return (
    <span className={`inline-flex items-center whitespace-nowrap px-2.5 py-1 rounded-full text-xs font-medium status-${status}`} data-testid={`status-badge-${status}`}>
      {labels[status] || status}
    </span>
  );
}

/** Application statuses (server: APPLICATION_STATUSES) and their badge styles. */
export const APPLICATION_STATUSES = ['Applied', 'Submitted', 'In Progress', 'Rejected', 'Hold', 'Inactive', 'Dropped', 'Not Interested', 'Closed'];

export const APP_STATUS_STYLES = {
  'Applied':        'bg-brand/10 text-brand-soft border border-brand/30',
  'Submitted':      'bg-brand/10 text-iris border border-brand/30',
  'In Progress':    'bg-ember/10 text-warning border border-ember/30',
  'Rejected':       'bg-ember/10 text-danger border border-ember/30',
  'Hold':           'bg-ember/10 text-ember border border-ember/30',
  'Inactive':       'bg-contrast/5 text-ink-2 border border-contrast/10',
  'Dropped':        'bg-ember/10 text-danger border border-ember/30',
  'Not Interested': 'bg-contrast/5 text-ink-2 border border-contrast/10',
  'Closed':         'bg-contrast/10 text-ink-2 border border-contrast/15',
};
